import React, { useCallback, useEffect, useState } from 'react';
import { fetchStokBenHareketler, fetchStokHareketler } from '../../services/stokV2Service';
import StokSiparisDetayFetchModal from './StokSiparisDetayFetchModal';
import StokGonderimDetayFetchModal from './StokGonderimDetayFetchModal';
import IadeFaturaDetayModal from '../IadeFaturaDetayModal';
import { fetchIadeFaturaDetay } from '../../services/iadeFaturaService';
import TableListFooter from '../TableListFooter';
import { formatCerideAdet, cerideAdetClass, STOK_TIPI_LABEL } from '../../utils/stokHareketLabels';
import './StokParcaCeridePanel.css';

const PAGE_SIZE = 25;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StokParcaCeridePanel = ({
  active,
  mode = 'ben',
  teknikServisId,
  icerikId,
}) => {
  const [sayfa, setSayfa] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hareketler, setHareketler] = useState([]);
  const [sayfalama, setSayfalama] = useState({ toplam: 0, toplamSayfa: 0, mevcutSayfa: 1, limit: PAGE_SIZE });
  const [detaySiparisId, setDetaySiparisId] = useState(null);
  const [detayGonderimId, setDetayGonderimId] = useState(null);
  const [detayIadeFatura, setDetayIadeFatura] = useState(null);

  const load = useCallback(async () => {
    if (!active || !icerikId) return;
    if (mode === 'admin' && !teknikServisId) {
      setHareketler([]);
      setError('Teknik servis bilgisi eksik');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = mode === 'admin'
        ? await fetchStokHareketler(teknikServisId, icerikId, { sayfa, limit: PAGE_SIZE })
        : await fetchStokBenHareketler(icerikId, { sayfa, limit: PAGE_SIZE });
      if (res?.basarili && res?.veri) {
        setHareketler(res.veri.hareketler || []);
        setSayfalama(res.veri.sayfalama || { toplam: 0, toplamSayfa: 0, mevcutSayfa: sayfa, limit: PAGE_SIZE });
      } else {
        setHareketler([]);
        setError(res?.mesaj || 'Veri alınamadı');
      }
    } catch (e) {
      setHareketler([]);
      setError(getErrorMessage(e, 'Liste yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [active, icerikId, teknikServisId, mode, sayfa]);

  useEffect(() => {
    if (active && icerikId) {
      setSayfa(1);
      setDetaySiparisId(null);
      setDetayGonderimId(null);
      setDetayIadeFatura(null);
    }
  }, [active, icerikId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRowClick = async (h) => {
    if (h.referansTip === 'siparis' && h.referansId) {
      setDetaySiparisId(h.referansId);
      setDetayGonderimId(null);
      setDetayIadeFatura(null);
      return;
    }
    if (h.referansTip === 'gonderim' && h.referansId) {
      setDetayGonderimId(h.referansId);
      setDetaySiparisId(null);
      setDetayIadeFatura(null);
      return;
    }
    if (h.referansTip === 'iadeFatura' && h.referansId) {
      try {
        const res = await fetchIadeFaturaDetay(h.referansId);
        if (res?.basarili && res?.veri) {
          setDetayIadeFatura(res.veri);
          setDetaySiparisId(null);
          setDetayGonderimId(null);
        }
      } catch {
        /* detay açılamadı */
      }
    }
  };

  if (!active) return null;

  return (
    <div className="stok-ceride-panel">
      {error && <div className="alert alert-danger" style={{ marginBottom: '12px' }}>{error}</div>}
      {loading ? (
        <p className="stok-ceride-panel__hint">Yükleniyor…</p>
      ) : hareketler.length === 0 ? (
        <p className="stok-ceride-panel__hint">Hareket kaydı yok.</p>
      ) : (
        <div className="stok-ceride-list">
          {hareketler.map((h) => {
            const clickable = ['siparis', 'gonderim', 'iadeFatura'].includes(h.referansTip);
            const refLine = [
              h.referansNo ? `#${h.referansNo}` : null,
              h.karsiBayiAdi || null,
            ].filter(Boolean).join(' · ');
            return (
              <div
                key={h.id}
                className={`stok-ceride-row${clickable ? ' stok-ceride-row--clickable' : ''}`}
                onClick={clickable ? () => handleRowClick(h) : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter') handleRowClick(h); } : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
              >
                <div className="stok-ceride__tarih">{formatTarih(h.olusturmaTarihi)}</div>
                <div>
                  <div className="stok-ceride__aciklama">{h.aciklama || h.hareketTipi}</div>
                  {refLine ? <div className="stok-ceride__ref">{refLine}</div> : null}
                  {h.not ? <div className="stok-ceride__not">{h.not}</div> : null}
                  {h.kullaniciAdi ? <div className="stok-ceride__kullanici">{h.kullaniciAdi}</div> : null}
                </div>
                <div className={`stok-ceride__adet ${cerideAdetClass(h.adet)}`}>
                  {formatCerideAdet(h.adet)}
                </div>
                <div className="stok-ceride__tip">{STOK_TIPI_LABEL[h.stokTipi] || h.stokTipi || '—'}</div>
                <div className="stok-ceride__kalan">{h.kalanStok ?? '—'}</div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && (
        <TableListFooter
          variant="compact"
          totalRecords={sayfalama.toplam ?? 0}
          currentPage={sayfalama.mevcutSayfa || sayfa}
          totalPages={Math.max(1, sayfalama.toplamSayfa || 1)}
          onPageChange={setSayfa}
          loading={loading}
        />
      )}

      {detaySiparisId && (
        <StokSiparisDetayFetchModal siparisId={detaySiparisId} onClose={() => setDetaySiparisId(null)} />
      )}
      {detayGonderimId && (
        <StokGonderimDetayFetchModal gonderimId={detayGonderimId} onClose={() => setDetayGonderimId(null)} />
      )}
      {detayIadeFatura && (
        <IadeFaturaDetayModal
          iadeFatura={detayIadeFatura}
          onClose={() => setDetayIadeFatura(null)}
          onRefresh={() => setDetayIadeFatura(null)}
        />
      )}
    </div>
  );
};

export default StokParcaCeridePanel;
